import { queryOptions } from "@tanstack/react-query";
import {
  fetchAskNexusAvailability,
  fetchAskNexusConversation,
  fetchAskNexusConversations,
} from "@/features/ask-nexus/api/ask-nexus.api";

/** Availability is deployment configuration: it changes with a deploy, not while someone works. */
const AVAILABILITY_STALE_TIME_MS = 5 * 60 * 1000;

/** Every key carries the tenant: switching organization never shows another tenant's conversations. */
export const askNexusKeys = {
  all: ["ask-nexus"] as const,
  /** The same for every organization, so not keyed by tenant. */
  availability: ["ask-nexus-availability"] as const,
  tenant: (tenantId: string) => ["ask-nexus", tenantId] as const,
  conversations: (tenantId: string) => ["ask-nexus", tenantId, "conversations"] as const,
  conversation: (tenantId: string, conversationId: string) =>
    ["ask-nexus", tenantId, "conversation", conversationId] as const,
} as const;

export function askNexusConversationsQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: askNexusKeys.conversations(tenantId),
    queryFn: fetchAskNexusConversations,
    enabled: tenantId.length > 0,
    meta: { skipGlobalError: true },
  });
}

/** Asked only for someone who could use Ask Nexus; a failed refresh keeps the last answer. */
export function askNexusAvailabilityQueryOptions(isWanted: boolean) {
  return queryOptions({
    queryKey: askNexusKeys.availability,
    queryFn: fetchAskNexusAvailability,
    enabled: isWanted,
    staleTime: AVAILABILITY_STALE_TIME_MS,
    meta: { skipGlobalError: true },
  });
}

/** Polls while the answer runs, at the interval the server asks for. */
export function askNexusConversationQueryOptions(tenantId: string, conversationId: string) {
  return queryOptions({
    queryKey: askNexusKeys.conversation(tenantId, conversationId),
    queryFn: () => fetchAskNexusConversation(conversationId),
    enabled: tenantId.length > 0 && conversationId.length > 0,
    refetchInterval: (query) => (query.state.data?.status === "running" ? query.state.data.retryAfterMs ?? false : false),
    meta: { skipGlobalError: true },
  });
}
